import {
  type ButtonHTMLAttributes,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// FilterBar — the top control of a DataTable (search + filters + sort)
// (design/components/data-table.html §7.1). A flex container with a SearchInput
// and FilterButtons; counts and active state carry text, not colour alone.

/** Flex container for a DataTable's top controls (SearchInput, FilterButtons,
 *  sort), wrapping on narrow widths. */
export function FilterBar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-wrap items-center gap-[9px]', className)}>{children}</div>
}

export interface SearchInputProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string
}

/** type=search input with a leading magnifier icon, sized to flex inside a
 *  FilterBar; supplies a default aria-label so no visible label is needed. */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { className, 'aria-label': ariaLabel, ...rest },
  ref,
) {
  const { t } = useTranslation('components')
  return (
    <span className="relative min-w-[200px] flex-1">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="pointer-events-none absolute left-[11px] top-1/2 size-[14px] -translate-y-1/2 text-text-3"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input
        ref={ref}
        type="search"
        aria-label={ariaLabel ?? t('ui.search')}
        className={cn(
          'w-full rounded-md border border-input-line bg-surface py-[9px] pl-8 pr-[11px] text-[12.5px] text-text outline-none placeholder:text-text-3',
          'focus-visible:border-brand-blue focus-visible:ring-[3.5px] focus-visible:ring-[color-mix(in_srgb,var(--brand-blue)_15%,transparent)]',
          className,
        )}
        {...rest}
      />
    </span>
  )
})
SearchInput.displayName = 'SearchInput'

export interface FilterButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean
  count?: number
  icon?: ReactNode
}

export const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>(function FilterButton(
  { active, count, icon, children, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-[7px] rounded-md border bg-surface px-[11px] py-2 text-[12px] font-medium transition-colors [&_svg]:size-[13px]',
        active
          ? 'border-brand-blue text-link [&_svg]:text-link'
          : 'border-line text-text-2 hover:bg-hover hover:text-text [&_svg]:text-text-3',
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
      {count != null && (
        <span className="rounded-pill bg-[color-mix(in_srgb,var(--brand-blue)_12%,transparent)] px-1.5 text-[10px] font-bold tabular-nums text-link">
          {count}
        </span>
      )}
    </button>
  )
})
FilterButton.displayName = 'FilterButton'
